// La musique dans l'application : un seul contexte Web Audio, ouvert au premier geste (les navigateurs l'exigent),
// mis en veille quand l'appli passe en arrière-plan. À chaque changement du jeu, le chef d'orchestre reçoit la
// nouvelle situation ; les évènements (dés, combat, niveau, rencontre…) deviennent des ponctuations.
// Réglages par appareil (marche/arrêt, volume) dans localStorage, protégés par try/catch.
import { useEffect, useState } from "preact/hooks";
import type { ClassId } from "../../shared/types";
import { getState, subscribe } from "../store";
import type { AppState } from "../store";
import { Conductor } from "./conductor";
import type { Stinger } from "./conductor";
import { situationOf } from "./score";
import type { MusicHint } from "./score";

export interface MusicPrefs { on: boolean; volume: number }

const PREFS_KEY = "veillee.musique";
const DEFAULT_PREFS: MusicPrefs = { on: true, volume: 0.7 };
const LOOKAHEAD = 1.2;
const TICK_MS = 250;
/** l'heure de Paris, la météo et l'échéance du tour changent sans message du serveur */
const REFRESH_MS = 5_000;
const NARRATION_GAP_MS = 6_000;
const MESSAGE_GAP_MS = 4_000;

let prefs: MusicPrefs = readPrefs();
let ctx: AudioContext | null = null;
let conductor: Conductor | null = null;
let loop: ReturnType<typeof setInterval> | null = null;
let hint: MusicHint = {};
let previous: AppState | null = null;
let lastNarrationAt = 0;
let lastMessageAt = 0;
let started = false;
const listeners = new Set<() => void>();

function readPrefs(): MusicPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const data = JSON.parse(raw) as Partial<MusicPrefs>;
    return {
      on: typeof data.on === "boolean" ? data.on : DEFAULT_PREFS.on,
      volume: typeof data.volume === "number" && data.volume >= 0 && data.volume <= 1 ? data.volume : DEFAULT_PREFS.volume,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function writePrefs(): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // stockage indisponible : le réglage vaut pour cette visite
  }
  for (const fn of listeners) fn();
}

/** La musique joue-t-elle en ce moment ? */
export function musicPlaying(): boolean {
  return prefs.on && ctx !== null && ctx.state === "running";
}

// ───────── contexte audio ─────────

function createContext(): AudioContext | null {
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    // iPhone : « ambient » se mêle aux autres sons et respecte le bouton silencieux, comme un jeu
    const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
    if (session) session.type = "ambient";
  } catch { /* pas de session audio : tant pis */ }
  try {
    return new Ctor({ latencyHint: "playback" });
  } catch {
    try {
      return new Ctor();
    } catch {
      return null;
    }
  }
}

/** À appeler pendant un geste (toucher, clic, touche) : ouvre ou réveille le contexte. */
function wake(): void {
  if (!prefs.on || document.hidden) return;
  if (!ctx) {
    ctx = createContext();
    if (!ctx) return;
    conductor = new Conductor(ctx, ctx.destination);
    conductor.setVolume(prefs.volume, ctx.currentTime);
    conductor.setSituation(situationOf(getState(), hint, Date.now()), ctx.currentTime);
    // vieux Safari : un tampon muet joué pendant le geste débloque le son
    const silent = ctx.createBufferSource();
    silent.buffer = ctx.createBuffer(1, 1, 22050);
    silent.connect(ctx.destination);
    silent.start(0);
    ctx.onstatechange = () => { for (const fn of listeners) fn(); };
  }
  if (ctx.state !== "running") {
    void ctx.resume().then(() => conductor?.resync(), () => undefined);
  }
  startLoop();
  for (const fn of listeners) fn();
}

function startLoop(): void {
  if (loop !== null) return;
  loop = setInterval(() => {
    if (!ctx || !conductor || ctx.state !== "running") return;
    conductor.schedule(ctx.currentTime + LOOKAHEAD);
  }, TICK_MS);
}

function stopLoop(): void {
  if (loop !== null) clearInterval(loop);
  loop = null;
}

function sleep(): void {
  stopLoop();
  if (ctx && ctx.state === "running") void ctx.suspend().catch(() => undefined);
}

// ───────── réglages ─────────

export function setMusicOn(on: boolean): void {
  prefs = { ...prefs, on };
  writePrefs();
  if (on) wake();
  else sleep();
}

export function setMusicVolume(volume: number): void {
  prefs = { ...prefs, volume: Math.max(0, Math.min(1, volume)) };
  writePrefs();
  if (ctx && conductor) conductor.setVolume(prefs.volume, ctx.currentTime);
}

/** Le bouton de musique : s'il est réglé sur « marche » mais encore muet (aucun geste), il la lance ; sinon il bascule. */
export function toggleMusic(): void {
  if (prefs.on && !musicPlaying()) wake();
  else setMusicOn(!prefs.on);
}

export function useMusic(): MusicPrefs & { playing: boolean } {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = (): void => setTick((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return { ...prefs, playing: musicPlaying() };
}

/** L'écran de création annonce la classe en cours de choix : la musique prend sa voix. */
export function musicHint(next: { classId?: ClassId | null }): void {
  hint = next;
  refresh();
}

// ───────── le jeu → la musique ─────────

function refresh(): void {
  if (!ctx || !conductor) return;
  conductor.setSituation(situationOf(getState(), hint, Date.now()), ctx.currentTime);
}

function play(st: Stinger): void {
  if (ctx && conductor && ctx.state === "running") conductor.stinger(st);
}

/** Ce qui vient d'arriver entre deux états du jeu, en ponctuations. */
export function stingersBetween(prev: AppState | null, next: AppState, now: number): Stinger[] {
  const out: Stinger[] = [];
  const a = prev?.view ?? null;
  const b = next.view;
  if (!b) return out;
  if (!a) {
    // le monde vient de s'ouvrir : le feu s'allume
    out.push({ kind: "allume" });
    return out;
  }

  // dés
  const lastDice = prev?.dice.length ? Math.max(...prev.dice.map((d) => d.id)) : 0;
  const fresh = next.dice.filter((d) => d.id > lastDice);
  if (fresh.length > 0) {
    const rolls = fresh[0].rolls;
    const verdict = rolls.find((r) => r.crit) ?? rolls.find((r) => typeof r.success === "boolean") ?? rolls[0];
    out.push({ kind: "des", success: typeof verdict?.success === "boolean" ? verdict.success : null, crit: verdict?.crit ?? null });
  }

  const me = b.character;
  // combat
  const wasFighting = a.combat?.status === "en_cours";
  const fighting = b.combat?.status === "en_cours";
  if (!wasFighting && fighting) out.push({ kind: "combatDebut" });
  if (wasFighting && !fighting) {
    const end = b.combat?.status;
    if (end === "victoire") out.push({ kind: "victoire" });
    else if (end === "defaite") out.push({ kind: "defaite" });
    else if (end === "fuite") out.push({ kind: "fuite" });
  }

  // voyage
  if (me && a.character) {
    if (!a.character.travel && me.travel) out.push({ kind: "depart" });
    if (a.character.travel && !me.travel) out.push({ kind: "arrivee" });
  }

  // donjon : une porte s'ouvre
  if (b.dungeon && (!a.dungeon || a.dungeon.room.id !== b.dungeon.room.id)) out.push({ kind: "porte" });

  // le récit
  const seen = new Set(a.feed.map((e) => e.id));
  for (const e of b.feed) {
    if (seen.has(e.id)) continue;
    const mine = !!me && e.actorId === me.id;
    if (e.kind === "niveau" && mine) out.push({ kind: "niveau" });
    else if (e.kind === "butin" && mine) out.push({ kind: "butin" });
    else if (e.kind === "mort") out.push({ kind: "mort" });
    else if (e.kind === "chronique") out.push({ kind: "chronique" });
    else if (e.kind === "narration" && now - lastNarrationAt > NARRATION_GAP_MS) {
      lastNarrationAt = now;
      out.push({ kind: "narration" });
    }
  }

  // le Conteur m'appelle
  const asked = new Set(a.sollicitations.map((x) => x.id));
  if (me && b.sollicitations.some((x) => !asked.has(x.id) && x.characterId === me.id)) out.push({ kind: "appel" });

  // une rencontre : quelqu'un arrive là où je suis (pas quand c'est moi qui viens d'arriver)
  if (a.location?.id === b.location?.id) {
    const here = new Set(a.othersHere.map((o) => o.id));
    const arrived = b.othersHere.find((o) => !here.has(o.id));
    if (arrived) {
      const classId = b.others.find((o) => o.id === arrived.id)?.classId ?? null;
      out.push({ kind: "rencontre", other: `${arrived.name}${arrived.sigil}`, classId });
    }
  }

  // un message d'un autre joueur
  const said = new Set(a.chat.map((m) => m.id));
  const message = b.chat.find((m) => !said.has(m.id) && m.playerId !== b.me.playerId);
  if (message && now - lastMessageAt > MESSAGE_GAP_MS) {
    lastMessageAt = now;
    const author = b.others.find((o) => o.playerName === message.name);
    out.push({ kind: "message", from: author ? `${author.name}${author.appearance.sigil}` : message.name });
  }
  return out;
}

function onState(): void {
  const next = getState();
  refresh();
  for (const st of stingersBetween(previous, next, Date.now())) play(st);
  previous = next;
}

/** À appeler une fois au lancement : écoute le jeu, les gestes et la visibilité de la page. */
export function initMusic(): void {
  if (started) return;
  started = true;
  previous = getState();
  subscribe(onState);
  const gesture = (e: Event): void => {
    // le bouton de musique décide lui-même (sinon un toucher pour couper relancerait aussitôt)
    const target = e.target as Element | null;
    if (target && typeof target.closest === "function" && target.closest("[data-musique]")) return;
    wake();
  };
  for (const type of ["pointerdown", "keydown", "touchend"]) window.addEventListener(type, gesture, { capture: true, passive: true });
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) sleep();
    else if (prefs.on && ctx) {
      // sans geste, iOS peut refuser : le prochain toucher réveillera la musique
      void ctx.resume().then(() => { conductor?.resync(); startLoop(); }, () => undefined);
    }
  });
  setInterval(refresh, REFRESH_MS);
}
